import * as React from 'react'
import { useSocketService } from '@/context/SocketServiceContext'
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircle } from '@fortawesome/pro-duotone-svg-icons/faCircle'
import { MenuItem, Box, Typography } from '@mui/material'
import Kedo from '@/Kedo.js'

interface IAvatarOptions {
    togglePresence?: boolean
    size?: number
    showPresence?: boolean
}

interface User {
    userId: string | number
    username: string
}

interface SelectUserPresenceProps {
    kedo: Kedo
    user: User
    props?: any
    onClick?: () => void
    options?: IAvatarOptions
    presence?: string
    onPresenceChange?: (presence: string) => void
}

const SelectUserPresence: React.FC<SelectUserPresenceProps> = ({
    kedo,
    user,
    options,
    presence: presenceProp,
    onPresenceChange,
}) => {
    const socketService = useSocketService()
    const [status, setStatus] = useState('')

    const currentStatus = presenceProp || status

    const updateStatus = (next: string) => {
        if (!next) {
            return
        }
        setStatus(next)
        onPresenceChange?.(next)
    }

    useEffect(() => {
        if (options?.togglePresence) {
            kedo.api()
                .get(kedo.api().getUserProfileEndpoint('me'))
                .then((result) => {
                    if (
                        result.data &&
                        result.data.private_settings &&
                        result.data.private_settings.presence
                    ) {
                        const saved = result.data.private_settings.presence
                        socketService.user
                            .setPresence(saved)
                            .then((resultPresence) => {
                                updateStatus(
                                    (typeof resultPresence === 'string' &&
                                        resultPresence) ||
                                        saved
                                )
                            })
                            .catch(() => updateStatus(saved))
                    }
                })
                .catch((errResponse) => console.log(errResponse))
        }
    }, [])

    useEffect(() => {
        const fromSocket = socketService.user.getPresence(user.userId)
        if (fromSocket && !presenceProp) {
            setStatus(fromSocket as unknown as string)
        }
    }, [
        user.userId,
        presenceProp,
        socketService,
        socketService.presenceRevision,
    ])

    const setPresence = (e, presence) => {
        if (e && e.stopPropagation) e.stopPropagation()

        const userData = {
            private_settings: { presence: presence },
        }

        updateStatus(presence)

        kedo.api()
            .patch(kedo.api().getUserProfileEndpoint('me'), userData)
            .then(() => {
                socketService.user
                    .setPresence(presence)
                    .then((resultPresence) => {
                        updateStatus(
                            (typeof resultPresence === 'string' &&
                                resultPresence) ||
                                presence
                        )
                    })
                    .catch(() => updateStatus(presence))
            })
            .catch((errResponse) => console.log(errResponse))
    }

    const presenceOptions: { key: string; color: string; label: string }[] = [
        { key: 'online', color: 'green', label: kedo.t('Online') || 'Online' },
        { key: 'away', color: 'orange', label: kedo.t('Away') || 'Away' },
        { key: 'busy', color: 'red', label: kedo.t('Busy') || 'Busy' },
        {
            key: 'invisible',
            color: 'gray',
            label: kedo.t('Invisible') || 'Invisible',
        },
    ]

    return (
        <>
            {options?.togglePresence ? (
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 1 }}>
                        {`${kedo.t('Set status')}:`}
                    </Typography>
                    {presenceOptions.map(({ key, color, label }) => {
                        const selected = currentStatus === key
                        return (
                            <MenuItem
                                key={key}
                                selected={selected}
                                onClick={(e) => setPresence(e, key)}
                                sx={{
                                    borderRadius: 1,
                                    my: 0.25,
                                    bgcolor: selected
                                        ? 'rgba(0, 0, 0, 0.08)'
                                        : 'transparent',
                                    '&.Mui-selected': {
                                        bgcolor: 'rgba(0, 0, 0, 0.08)',
                                    },
                                    '&.Mui-selected:hover': {
                                        bgcolor: 'rgba(0, 0, 0, 0.12)',
                                    },
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faCircle}
                                    style={{
                                        color,
                                        fontSize: 10,
                                        marginRight: 8,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        opacity: 1,
                                        fontWeight: selected ? 600 : 400,
                                    }}
                                >
                                    {label}
                                </Typography>
                            </MenuItem>
                        )
                    })}
                </Box>
            ) : null}
        </>
    )
}

export default SelectUserPresence
